import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import axiosInstance from '../../axiosConfig';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import '../../assets/CSS/Dashboard.css';
import './LoopPunctureReadiness.css';

const STATUS_META = {
  READY: { label: 'Taiyar', icon: 'bi-check-circle-fill', className: 'ready' },
  PENDING: { label: 'Banana Baaki', icon: 'bi-hourglass-split', className: 'pending' },
  UPCOMING: { label: 'Screw Line Mein', icon: 'bi-clock-history', className: 'upcoming' },
  BLOCKED: { label: 'Ruka Hua', icon: 'bi-slash-circle-fill', className: 'blocked' },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.PENDING;
  return (
    <span className={`lpr-status-pill ${meta.className}`}>
      <i className={`bi ${meta.icon}`} /> {meta.label}
    </span>
  );
};

const INDIA_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ERP_INDIA_DATE_TIME = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s*(?::)?\s*(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i;

const formatDateTime = value => {
  if (!value) return '—';

  // ConfirmTime stores ERP timestamps as dd/MM/yyyy : hh:mm AM/PM text.
  // Parsing that text with new Date() makes browsers treat dd as MM, so
  // format it explicitly as Indian local time without applying a time shift.
  if (typeof value === 'string') {
    const match = value.trim().match(ERP_INDIA_DATE_TIME);
    if (match) {
      const [, dayText, monthText, yearText, hourText, minuteText, meridiemText] = match;
      const day = Number(dayText);
      const month = Number(monthText);
      const hour = Number(hourText);
      const minute = Number(minuteText);

      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && hour >= 1 && hour <= 12 && minute <= 59) {
        return `${String(day).padStart(2, '0')} ${INDIA_MONTHS[month - 1]} ${yearText}, ${String(hour).padStart(2, '0')}:${minuteText} ${meridiemText.toUpperCase()}`;
      }
    }
  }

  // Readiness timestamps are real SQL datetime values; render them in IST.
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ItemReadiness = ({
  title,
  subtitle,
  icon,
  status,
  confirmedBy,
  readyAt,
  ticketId,
  ticketStatus,
  canConfirm,
  canRaiseTicket,
  busy,
  onConfirm,
  onTicket,
}) => (
  <div className={`lpr-item-panel ${status === 'READY' ? 'is-ready' : 'is-pending'}`}>
    <div className="lpr-item-heading">
      <span className="lpr-item-icon"><i className={`bi ${icon}`} /></span>
      <div>
        <h6>{title}</h6>
        <small>{subtitle}</small>
      </div>
      <StatusPill status={status} />
    </div>

    {status === 'READY' ? (
      <div className="lpr-ready-detail">
        <span><i className="bi bi-person-check" /> {confirmedBy || 'Confirmed'}</span>
        <span><i className="bi bi-clock" /> {formatDateTime(readyAt)}</span>
      </div>
    ) : (
      <div className="lpr-item-actions">
        {canConfirm && (
          <button className="btn lpr-ready-btn" disabled={busy} onClick={onConfirm}>
            {busy ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-check2-circle" />}
            Taiyar Hai
          </button>
        )}
        {canRaiseTicket && !ticketId && (
          <button
            className="btn lpr-ticket-btn"
            disabled={busy}
            onClick={onTicket}
          >
            <i className="bi bi-ticket-perforated" /> Ticket Bhejein
          </button>
        )}
        {ticketId && (
          <span className="lpr-ticket-reference">
            <i className="bi bi-ticket-detailed-fill" /> Ticket #{ticketId}
            {ticketStatus ? ` · ${ticketStatus.replaceAll('_', ' ')}` : ''}
          </span>
        )}
      </div>
    )}
  </div>
);

const LoopPunctureReadiness = () => {
  const [records, setRecords] = useState([]);
  const [access, setAccess] = useState({});
  const [actor, setActor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);

  const fetchReadiness = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const response = await axiosInstance.get('/api/wip/loop-puncture-readiness');
      setRecords(response.data.records || []);
      setAccess(response.data.access || {});
      setActor(response.data.actor || null);
    } catch (error) {
      const message = error.response?.data?.message || 'Readiness queue load nahi ho paayi.';
      if (showLoader) Swal.fire('Unable to load', message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReadiness();
    const refreshInterval = window.setInterval(() => fetchReadiness(false), 30000);
    return () => window.clearInterval(refreshInterval);
  }, [fetchReadiness]);

  const summary = useMemo(() => records.reduce((acc, row) => {
    acc.total += 1;
    if (row.OverallReadiness === 'BOTH_READY') acc.ready += 1;
    else if (row.OverallReadiness === 'PARTIAL_READY') acc.partial += 1;
    else acc.pending += 1;
    return acc;
  }, { total: 0, ready: 0, partial: 0, pending: 0 }), [records]);

  const filteredRecords = useMemo(() => {
    const query = search.trim().toLowerCase();
    return records.filter(row => {
      const matchesSearch = !query
        || String(row.LOT_ID || '').toLowerCase().includes(query)
        || String(row.ScrewSupervisor || '').toLowerCase().includes(query);
      const matchesFilter = filter === 'ALL'
        || (filter === 'READY' && row.OverallReadiness === 'BOTH_READY')
        || (filter === 'ATTENTION' && row.OverallReadiness !== 'BOTH_READY')
        || (filter === 'PARTIAL' && row.OverallReadiness === 'PARTIAL_READY');
      return matchesSearch && matchesFilter;
    });
  }, [records, search, filter]);

  const confirmReadiness = async (row, readinessType, label) => {
    const result = await Swal.fire({
      icon: 'question',
      title: `${label} taiyar hai?`,
      html: `<b>${row.LOT_ID}</b><br><span style="color:#64748b">Material physically taiyar ho tabhi confirm karein.</span>`,
      showCancelButton: true,
      confirmButtonText: 'Haan, Taiyar Hai',
      cancelButtonText: 'Nahi',
      confirmButtonColor: '#16a34a',
    });
    if (!result.isConfirmed) return;

    const key = `${row.ScrewConfirmTimeID}-${readinessType}`;
    setBusyKey(key);
    try {
      const response = await axiosInstance.post(
        `/api/wip/loop-puncture-readiness/${row.ScrewConfirmTimeID}/confirm`,
        { readinessType }
      );
      await fetchReadiness(false);
      Swal.fire({ icon: 'success', title: 'Status Updated', text: response.data.message, timer: 1800, showConfirmButton: false });
    } catch (error) {
      Swal.fire('Not saved', error.response?.data?.message || 'Please try again.', 'error');
    } finally {
      setBusyKey('');
    }
  };

  const raiseTicket = async (row, readinessType, label) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: `${label} ka ticket bhejein?`,
      html: `<b>LOT: ${row.LOT_ID}</b><br><span style="color:#64748b">ANWAR ko material taiyar karne ka 12-hour ticket jayega.</span>`,
      showCancelButton: true,
      confirmButtonText: 'Haan, Ticket Bhejein',
      cancelButtonText: 'Nahi',
      confirmButtonColor: '#dc2626',
    });
    if (!result.isConfirmed) return;

    const key = `${row.ScrewConfirmTimeID}-${readinessType}`;
    setBusyKey(key);
    try {
      const response = await axiosInstance.post(
        `/api/wip/loop-puncture-readiness/${row.ScrewConfirmTimeID}/ticket`,
        { readinessType }
      );
      await fetchReadiness(false);
      Swal.fire('Ticket Raised', response.data.message, 'success');
    } catch (error) {
      Swal.fire('Ticket not raised', error.response?.data?.message || 'Please try again.', 'error');
    } finally {
      setBusyKey('');
    }
  };

  const instruction = access.canRaiseTicket && !access.canConfirmBeltLoop && !access.canConfirmPuncture
    ? 'Peela “Banana Baaki” dikhe toh Ticket Bhejein. Dono item hara “Taiyar” ho toh Screwing shuru karein.'
    : 'Jis item par peela “Banana Baaki” hai, material ready karke hara “Taiyar Hai” button dabayein.';

  return (
    <div className="d-flex dashboard">
      <div className={isSidebarVisible ? 'sidebar-container shadow-sm' : 'sidebar-hidden'}><Sidebar /></div>
      <div className="flex-grow-1 lpr-page-shell">
        <Header toggleSidebar={() => setIsSidebarVisible(value => !value)} isSidebarVisible={isSidebarVisible} />

        <main className="lpr-main">
          <section className="lpr-hero">
            <div>
              <span className="lpr-eyebrow"><i className="bi bi-diagram-3-fill" /> Screw Material</span>
              <h1>Loop / Puncture ka Kaam</h1>
              <p>Peela matlab kaam baaki. Hara matlab material taiyar.</p>
            </div>
            <button className="btn lpr-refresh-btn" onClick={() => fetchReadiness()} disabled={loading}>
              <i className={`bi bi-arrow-clockwise ${loading ? 'lpr-spin' : ''}`} /> Dobara Check Karein
            </button>
          </section>

          <section className="lpr-guide">
            <div className="lpr-guide-icon"><i className="bi bi-lightbulb-fill" /></div>
            <div>
              <strong>{actor?.Name || 'Supervisor'}, aapko kya karna hai?</strong>
              <p>{instruction}</p>
            </div>
            <div className="lpr-live-badge"><span /> Live LOT List</div>
          </section>

          <section className="lpr-summary-grid">
            <button className={`lpr-summary-card total ${filter === 'ALL' ? 'active' : ''}`} onClick={() => setFilter('ALL')}>
              <span className="lpr-summary-icon"><i className="bi bi-box-seam-fill" /></span>
              <span><small>Sabhi LOT</small><strong>{summary.total}</strong></span>
            </button>
            <button className={`lpr-summary-card pending ${filter === 'ATTENTION' ? 'active' : ''}`} onClick={() => setFilter('ATTENTION')}>
              <span className="lpr-summary-icon"><i className="bi bi-exclamation-triangle-fill" /></span>
              <span><small>Kaam Baaki</small><strong>{summary.pending + summary.partial}</strong></span>
            </button>
            <button className={`lpr-summary-card partial ${filter === 'PARTIAL' ? 'active' : ''}`} onClick={() => setFilter('PARTIAL')}>
              <span className="lpr-summary-icon"><i className="bi bi-circle-half" /></span>
              <span><small>Ek Taiyar</small><strong>{summary.partial}</strong></span>
            </button>
            <button className={`lpr-summary-card ready ${filter === 'READY' ? 'active' : ''}`} onClick={() => setFilter('READY')}>
              <span className="lpr-summary-icon"><i className="bi bi-check-circle-fill" /></span>
              <span><small>Dono Taiyar</small><strong>{summary.ready}</strong></span>
            </button>
          </section>

          <section className="lpr-toolbar">
            <div className="lpr-search">
              <i className="bi bi-search" />
              <input value={search} onChange={event => setSearch(event.target.value)} placeholder="LOT number khojein..." />
              {search && <button onClick={() => setSearch('')} aria-label="Clear search"><i className="bi bi-x-lg" /></button>}
            </div>
            <div className="lpr-filter-copy">
              <strong>{filteredRecords.length}</strong> / {records.length} LOT dikh rahe hain
            </div>
          </section>

          {loading ? (
            <div className="lpr-state-card">
              <div className="spinner-border text-primary" role="status" />
              <h5>Loading Screw queue...</h5>
              <p>Latest LOT readiness status la rahe hain.</p>
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="lpr-state-card">
              <i className="bi bi-inbox-fill" />
              <h5>No LOTs found</h5>
              <p>Current search/filter me koi Screw queue LOT nahi hai.</p>
              <button className="btn btn-outline-primary" onClick={() => { setSearch(''); setFilter('ALL'); }}>Show all LOTs</button>
            </div>
          ) : (
            <section className="lpr-lot-grid">
              {filteredRecords.map(row => {
                const bothReady = row.OverallReadiness === 'BOTH_READY';
                const ticketEligible = row.ScrewLotStatus === 'READY' && !bothReady;
                return (
                  <article className={`lpr-lot-card ${bothReady ? 'complete' : ticketEligible ? 'critical' : 'attention'}`} key={row.ScrewConfirmTimeID}>
                    <header className="lpr-lot-header">
                      <div>
                        <small>LOT ID</small>
                        <h3>{row.LOT_ID}</h3>
                      </div>
                      <span className={`lpr-overall-badge ${bothReady ? 'ready' : 'pending'}`}>
                        <i className={`bi ${bothReady ? 'bi-shield-check' : 'bi-exclamation-diamond-fill'}`} />
                        {bothReady ? 'Dono Taiyar' : row.OverallReadiness === 'PARTIAL_READY' ? 'Ek Item Baaki' : 'Dono Banana Baaki'}
                      </span>
                    </header>

                    <div className="lpr-lot-meta">
                      <div><small>Screw In-charge</small><strong><i className="bi bi-person-badge" /> {row.ScrewSupervisor || '—'}</strong></div>
                      <div><small>Kitni Qty</small><strong><i className="bi bi-box2-fill" /> {Number(row.RequiredQuantity || 0).toLocaleString('en-IN')}</strong></div>
                      <div><small>Screw Stage</small><StatusPill status={row.ScrewLotStatus} /></div>
                      <div><small>Queue Time</small><strong><i className="bi bi-calendar-event" /> {formatDateTime(row.ScrewQueueTime)}</strong></div>
                      <div><small>Ready Time</small><strong><i className="bi bi-check2-circle" /> {row.ScrewLotStatus === 'READY' ? (row.ScrewAcceptedTime ? formatDateTime(row.ScrewAcceptedTime) : 'Accepted - time unavailable') : 'Not Ready'}</strong></div>
                    </div>

                    {ticketEligible && access.canRaiseTicket && (
                      <div className="lpr-warning-strip">
                        <i className="bi bi-exclamation-octagon-fill" />
                        RED WARNING: Screw LOT is READY but material is PENDING. Raise a ticket for the pending item.
                      </div>
                    )}

                    {!bothReady && row.ScrewLotStatus === 'UPCOMING' && access.canRaiseTicket && (
                      <div className="lpr-upcoming-strip">
                        <i className="bi bi-clock-history" />
                        Material preparation can continue now. Ticket action will activate after the Screw LOT is READY/Accepted.
                      </div>
                    )}

                    {!bothReady && (access.canConfirmBeltLoop || access.canConfirmPuncture) && (
                      <div className="lpr-worker-help-strip">
                        <span>ANWAR</span>
                        Peela item physically ready karke uska hara “Taiyar Hai” button dabayein.
                      </div>
                    )}

                    <div className="lpr-items-grid">
                      <ItemReadiness
                        title="Belt Loop"
                        subtitle="Belt ka loop material"
                        icon="bi-bezier2"
                        status={row.BeltLoopStatus}
                        confirmedBy={row.BeltLoopConfirmedBy}
                        readyAt={row.BeltLoopReadyAt}
                        ticketId={row.BeltLoopTicketID}
                        ticketStatus={row.BeltLoopTicketStatus}
                        canConfirm={access.canConfirmBeltLoop}
                        canRaiseTicket={access.canRaiseTicket && row.ScrewLotStatus === 'READY'}
                        busy={busyKey === `${row.ScrewConfirmTimeID}-BELT_LOOP`}
                        onConfirm={() => confirmReadiness(row, 'BELT_LOOP', 'Belt Loop')}
                        onTicket={() => raiseTicket(row, 'BELT_LOOP', 'Belt Loop')}
                      />
                      <ItemReadiness
                        title="Puncture / Buckle"
                        subtitle="Puncture aur buckle material"
                        icon="bi-record-circle-fill"
                        status={row.PunctureStatus}
                        confirmedBy={row.PunctureConfirmedBy}
                        readyAt={row.PunctureReadyAt}
                        ticketId={row.PunctureTicketID}
                        ticketStatus={row.PunctureTicketStatus}
                        canConfirm={access.canConfirmPuncture}
                        canRaiseTicket={access.canRaiseTicket && row.ScrewLotStatus === 'READY'}
                        busy={busyKey === `${row.ScrewConfirmTimeID}-PUNCTURE`}
                        onConfirm={() => confirmReadiness(row, 'PUNCTURE', 'Puncture / Buckle')}
                        onTicket={() => raiseTicket(row, 'PUNCTURE', 'Puncture / Buckle')}
                      />
                    </div>
                  </article>
                );
              })}
            </section>
          )}
        </main>
      </div>
    </div>
  );
};

export default LoopPunctureReadiness;
